import React, { useState } from 'react';
import { 
  MousePointer2, 
  Square, 
  Circle, 
  Triangle,
  StickyNote,
  Type,
  Pencil,
  Eraser,
  Hand,
  ZoomIn,
  ZoomOut,
  Download,
  Upload,
  Trash2,
  Undo,
  Redo,
  ArrowRight,
  Hexagon,
  Crosshair
} from 'lucide-react';
import { Button } from '../design-system';

export const CanvasToolbar = ({ 
  activeTool, 
  onToolChange, 
  onZoomIn, 
  onZoomOut,
  onCenterView,
  onClear,
  onUndo,
  onRedo,
  onExport,
  onImport,
  canUndo,
  canRedo,
  selectedColor,
  onColorChange,
  selectedStroke,
  onStrokeChange,
  strokeWidth,
  onStrokeWidthChange,
  textFontSize,
  onTextFontSizeChange,
  isTextBold,
  onToggleTextBold,
  isTextItalic,
  onToggleTextItalic,
  isTextStrikethrough,
  onToggleTextStrikethrough,
  isTextContext
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const tools = [
    { id: 'select', icon: MousePointer2, label: 'Select' },
    { id: 'pan', icon: Hand, label: 'Pan' },
    { id: 'sticky', icon: StickyNote, label: 'Sticky Note' },
    { id: 'text', icon: Type, label: 'Text' },
    { id: 'rectangle', icon: Square, label: 'Rectangle' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'triangle', icon: Triangle, label: 'Triangle' },
    { id: 'mindmap', icon: Hexagon, label: 'Mindmap Node' },
    { id: 'connector', icon: ArrowRight, label: 'Connector' },
    { id: 'pencil', icon: Pencil, label: 'Draw' },
    { id: 'eraser', icon: Eraser, label: 'Eraser' },
  ];

  const ToolbarContent = () => (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Drawing Tools */}
      <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <button
              key={tool.id}
              onClick={() => onToolChange(tool.id)}
              className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                activeTool === tool.id ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
              }`}
              title={tool.label}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
      </div>

      {/* History Controls */}
      <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
          title="Undo"
        >
          <Undo className="h-5 w-5" />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
          title="Redo"
        >
          <Redo className="h-5 w-5" />
        </button>
      </div>

      {/* Zoom & View Controls */}
      <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
        <button
          onClick={onZoomOut}
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-700"
          title="Zoom Out"
        >
          <ZoomOut className="h-5 w-5" />
        </button>
        <button
          onClick={onZoomIn}
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-700"
          title="Zoom In"
        >
          <ZoomIn className="h-5 w-5" />
        </button>
        <button
          onClick={onCenterView}
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-700"
          title="Center View"
        >
          <Crosshair className="h-5 w-5" />
        </button>
      </div>

      {/* File Operations */}
      <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
        <button
          onClick={onExport}
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-700"
          title="Export Canvas"
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          onClick={onImport}
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-700"
          title="Import Canvas"
        >
          <Upload className="h-5 w-5" />
        </button>
      </div>

      {/* Clear */}
      <button
        onClick={onClear}
        className="p-2 rounded hover:bg-red-100 transition-colors text-red-600"
        title="Clear Canvas"
      >
        <Trash2 className="h-5 w-5" />
      </button>

      {/* Color & Stroke Controls */}
      <div className="flex items-center gap-2 border-l border-gray-200 pl-2 ml-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Fill</label>
          <input
            type="color"
            value={selectedColor}
            onChange={(e) => onColorChange(e.target.value)}
            className="w-10 h-8 rounded cursor-pointer border border-gray-300"
            title="Fill Color"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Stroke</label>
          <input
            type="color"
            value={selectedStroke}
            onChange={(e) => onStrokeChange(e.target.value)}
            className="w-10 h-8 rounded cursor-pointer border border-gray-300"
            title="Stroke Color"
          />
        </div>
        
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Width</label>
          <select
            value={strokeWidth}
            onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
            className="px-2 py-1 text-sm border border-gray-300 rounded cursor-pointer"
            title="Stroke Width"
          >
            <option value="1">1px</option>
            <option value="2">2px</option>
            <option value="3">3px</option>
            <option value="4">4px</option>
            <option value="6">6px</option>
            <option value="8">8px</option>
          </select>
        </div>
      </div>

      {/* Text Controls */}
      <div className={`flex items-center gap-2 border-l border-gray-200 pl-2 ml-2 ${
        isTextContext ? '' : 'opacity-50'
      }`}>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Text size</label>
          <select
            value={textFontSize}
            onChange={(e) => onTextFontSizeChange(Number(e.target.value))}
            className="px-2 py-1 text-sm border border-gray-300 rounded cursor-pointer min-w-[72px]"
            title="Text Font Size"
            disabled={!isTextContext}
          >
            <option value="12">12</option>
            <option value="14">14</option>
            <option value="16">16</option>
            <option value="18">18</option>
            <option value="20">20</option>
            <option value="24">24</option>
            <option value="32">32</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Style</label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onToggleTextBold}
              className={`px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 ${
                isTextBold ? 'bg-gray-200 font-semibold' : 'bg-white'
              }`}
              title="Bold"
              disabled={!isTextContext}
            >
              B
            </button>
            <button
              type="button"
              onClick={onToggleTextItalic}
              className={`px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 ${
                isTextItalic ? 'bg-gray-200 italic' : 'bg-white'
              }`}
              title="Italic"
              disabled={!isTextContext}
            >
              I
            </button>
            <button
              type="button"
              onClick={onToggleTextStrikethrough}
              className={`px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 ${
                isTextStrikethrough ? 'bg-gray-200 line-through' : 'bg-white'
              }`}
              title="Strikethrough"
              disabled={!isTextContext}
            >
              S
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop toolbar (large screens) */}
      <div className="hidden lg:block bg-white border-b border-gray-200 px-4 py-2">
        <ToolbarContent />
      </div>

      {/* Mobile / tablet toolbar drawer trigger and drawer */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setIsMobileOpen(true)}
          className="fixed bottom-4 right-4 z-40 px-3 py-2 rounded-full shadow-lg bg-white border border-gray-200 text-sm font-medium text-gray-700 flex items-center gap-2"
        >
          <Type className="h-4 w-4" />
          <span>Tools</span>
        </button>

        {isMobileOpen && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/30">
            <div className="bg-white border-t border-gray-200 rounded-t-2xl px-3 py-2 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Canvas tools</span>
                <button
                  type="button"
                  onClick={() => setIsMobileOpen(false)}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  aria-label="Close tools"
                >
                  ✕
                </button>
              </div>
              <ToolbarContent />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
